import { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import PageLoader from './PageLoader';
import { buildCompactTimelineChartOptions } from './compactTimelineChart';
import './GrossYieldTimelineSection.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
);

const ZILLOW_HOUSING_DATA_DOCS_URL = 'https://www.zillow.com/research/data/';
const ZILLOW_MARKET_HEAT_METHODOLOGY_URL = 'https://www.zillow.com/research/market-heat-index-methodology-34057/';

function formatCurrencyLabel(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: numeric >= 100000 ? 1 : 0,
  }).format(numeric);
}

function formatFullCurrencyLabel(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(numeric);
}

function formatPercentShareLabel(value, digits = 1) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(numeric);
}

function formatRuleRatioLabel(value, digits = 4) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toFixed(digits);
}

function formatHistoryLabel(months, emptyLabel = 'History pending') {
  const count = Number(months);
  if (!Number.isFinite(count) || count <= 0) return emptyLabel;
  return `${count} ${count === 1 ? 'month' : 'months'} loaded`;
}

function isFiniteSeriesValue(value) {
  if (value == null) return false;
  return Number.isFinite(Number(value));
}

function toFiniteNumbers(values) {
  return values
    .filter((value) => isFiniteSeriesValue(value))
    .map((value) => Number(value));
}

function trimSeriesToValueWindow(series, valueKeys) {
  if (!Array.isArray(series) || !series.length) return [];

  const hasRelevantValue = (item) => valueKeys.some((key) => isFiniteSeriesValue(item?.[key]));
  const firstIndex = series.findIndex(hasRelevantValue);
  if (firstIndex < 0) return [];

  let lastIndex = series.length - 1;
  while (lastIndex >= firstIndex && !hasRelevantValue(series[lastIndex])) {
    lastIndex -= 1;
  }

  return series.slice(firstIndex, lastIndex + 1);
}

function roundAxisBoundary(value, direction) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  if (numeric === 0) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(Math.abs(numeric)));
  const step = magnitude / 2;
  if (!Number.isFinite(step) || step <= 0) return numeric;
  return direction === 'down'
    ? Math.floor(numeric / step) * step
    : Math.ceil(numeric / step) * step;
}

function buildParityAlignedPriceDomains(homeValues, bottomTierValues, rentValues) {
  const priceEquivalentValues = [
    ...toFiniteNumbers(homeValues),
    ...toFiniteNumbers(bottomTierValues),
    ...toFiniteNumbers(rentValues).map((value) => value * 100),
  ];

  if (!priceEquivalentValues.length) {
    return {
      yHomeMin: undefined,
      yHomeMax: undefined,
      yRentMin: undefined,
      yRentMax: undefined,
    };
  }

  const rawMin = Math.min(...priceEquivalentValues);
  const rawMax = Math.max(...priceEquivalentValues);
  const span = rawMax - rawMin;
  const padding = span > 0
    ? Math.max(span * 0.08, rawMax * 0.015)
    : Math.max(rawMax * 0.08, 1000);
  const paddedMin = Math.max(0, rawMin - padding);
  const paddedMax = rawMax + padding;
  const yHomeMin = roundAxisBoundary(paddedMin, 'down') ?? paddedMin;
  const yHomeMax = roundAxisBoundary(paddedMax, 'up') ?? paddedMax;

  return {
    yHomeMin,
    yHomeMax,
    yRentMin: yHomeMin / 100,
    yRentMax: yHomeMax / 100,
  };
}

function buildCoverageStatusItems(payload, series) {
  // The 1% Rule coverage pill was removed: it is derived from the home-price and
  // rent coverage shown beside it, so it repeated information without adding any.
  const homeMonths = payload?.homeMonthsLoaded ?? series.filter((item) => item?.homePrice != null).length;
  const rentMonths = payload?.rentMonthsLoaded ?? series.filter((item) => item?.rentPrice != null).length;
  return [
    {
      label: 'Home Prices',
      value: formatHistoryLabel(homeMonths, 'No months loaded'),
    },
    {
      label: 'Rent Prices',
      value: formatHistoryLabel(rentMonths, 'No months loaded'),
    },
  ];
}

function describeMarketHeatLevel(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  if (numeric >= 70) return 'Strong sellers market';
  if (numeric >= 55) return 'Sellers market';
  if (numeric >= 44) return 'Neutral market';
  if (numeric >= 28) return 'Buyers market';
  return 'Strong buyers market';
}

function sortTooltipItemsByVisualOrder(left, right) {
  const leftY = Number(left?.element?.y);
  const rightY = Number(right?.element?.y);
  if (!Number.isFinite(leftY) || !Number.isFinite(rightY)) return 0;
  return leftY - rightY;
}

function buildRuleRatioChart(payload) {
  const series = trimSeriesToValueWindow(
    Array.isArray(payload?.series) ? payload.series : [],
    ['ruleRatio', 'trailing12MonthRuleRatio'],
  );
  const labels = series.map((item) => item.periodLabel);
  const monthlyValues = series.map((item) => item.ruleRatio);
  const trailingValues = series.map((item) => item.trailing12MonthRuleRatio);
  const monthlyPointCount = monthlyValues.filter((value) => value != null).length;
  const trailingPointCount = trailingValues.filter((value) => value != null).length;

  const datasets = [
    {
      label: 'Monthly 1% Rule',
      data: monthlyValues,
      borderColor: '#1d4ed8',
      backgroundColor: 'rgba(29, 78, 216, 0.10)',
      fill: true,
      borderWidth: 2.2,
      pointRadius: monthlyPointCount === 1 ? 4 : 0,
      pointHoverRadius: monthlyPointCount === 1 ? 5 : 3,
      pointBackgroundColor: '#1d4ed8',
      pointBorderWidth: 0,
      showLine: monthlyPointCount >= 2,
      spanGaps: true,
      tension: 0.28,
    },
    {
      label: 'Trailing 12m Avg',
      data: trailingValues,
      borderColor: '#0f766e',
      backgroundColor: 'transparent',
      fill: false,
      borderWidth: 2,
      borderDash: [8, 6],
      pointRadius: trailingPointCount === 1 ? 4 : 0,
      pointHoverRadius: trailingPointCount === 1 ? 5 : 3,
      pointBackgroundColor: '#0f766e',
      pointBorderWidth: 0,
      showLine: trailingPointCount >= 2,
      spanGaps: true,
      tension: 0.22,
    },
  ];

  return {
    data: {
      labels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#64748b',
            boxWidth: 18,
            boxHeight: 2,
            usePointStyle: false,
          },
        },
        tooltip: {
          itemSort: sortTooltipItemsByVisualOrder,
          callbacks: {
            label(context) {
              const numeric = Number(context.parsed.y);
              return `${context.dataset.label}: ${formatRuleRatioLabel(numeric)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#64748b',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            color: 'rgba(148, 163, 184, 0.18)',
          },
          ticks: {
            color: '#64748b',
            callback(value) {
              return formatRuleRatioLabel(value);
            },
          },
        },
      },
    },
  };
}

function buildHomeRentChart(payload) {
  const series = Array.isArray(payload?.series) ? payload.series : [];
  const labels = series.map((item) => item.periodLabel);
  const homeValues = series.map((item) => item.homePrice);
  const bottomTierValues = series.map((item) => item.bottomTierHomePrice);
  const rentValues = series.map((item) => item.rentPrice);
  const alignedDomains = buildParityAlignedPriceDomains(homeValues, bottomTierValues, rentValues);
  const hasHomeSeries = homeValues.filter((value) => value != null).length >= 2;
  const hasBottomTierSeries = bottomTierValues.filter((value) => value != null).length >= 2;
  const rentPointCount = rentValues.filter((value) => value != null).length;
  const hasRentSeries = rentPointCount >= 1;

  const datasets = [
    hasHomeSeries ? {
      label: 'Home Price',
      data: homeValues,
      yAxisID: 'yHome',
      borderColor: '#0f766e',
      backgroundColor: 'rgba(15, 118, 110, 0.08)',
      fill: false,
      borderWidth: 2.3,
      pointRadius: 0,
      pointHoverRadius: 3,
      pointBackgroundColor: '#0f766e',
      pointBorderWidth: 0,
      tension: 0.26,
    } : null,
  ].filter(Boolean);

  if (hasRentSeries) {
    datasets.push({
      label: 'Rent Price · Zillow',
      data: rentValues,
      yAxisID: 'yRent',
      borderColor: '#c2410c',
      backgroundColor: 'rgba(194, 65, 12, 0.08)',
      fill: false,
      borderWidth: 2.1,
      pointRadius: rentPointCount === 1 ? 4 : 0,
      pointHoverRadius: rentPointCount === 1 ? 5 : 3,
      pointBackgroundColor: '#c2410c',
      pointBorderWidth: 0,
      showLine: rentPointCount >= 2,
      spanGaps: true,
      tension: 0.24,
    });
  }

  if (hasBottomTierSeries) {
    datasets.push({
      label: 'Bottom-Tier Home Price',
      data: bottomTierValues,
      yAxisID: 'yHome',
      borderColor: '#38bdf8',
      backgroundColor: 'rgba(56, 189, 248, 0.08)',
      fill: false,
      borderWidth: 1.9,
      borderDash: [9, 5],
      pointRadius: 0,
      pointHoverRadius: 3,
      pointBackgroundColor: '#38bdf8',
      pointBorderWidth: 0,
      spanGaps: true,
      tension: 0.24,
    });
  }

  return {
    data: {
      labels,
      datasets,
    },
    hasBottomTierSeries,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#64748b',
            boxWidth: 18,
            boxHeight: 2,
            usePointStyle: false,
          },
        },
        tooltip: {
          itemSort: sortTooltipItemsByVisualOrder,
          callbacks: {
            label(context) {
              return `${context.dataset.label}: ${formatFullCurrencyLabel(context.parsed.y)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#64748b',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
        },
        yHome: {
          type: 'linear',
          position: 'left',
          beginAtZero: false,
          min: alignedDomains.yHomeMin,
          max: alignedDomains.yHomeMax,
          grid: {
            color: 'rgba(148, 163, 184, 0.18)',
          },
          ticks: {
            color: '#0f766e',
            callback(value) {
              return formatCurrencyLabel(value);
            },
          },
        },
        yRent: {
          type: 'linear',
          position: 'right',
          beginAtZero: false,
          min: alignedDomains.yRentMin,
          max: alignedDomains.yRentMax,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            color: '#c2410c',
            callback(value) {
              return formatFullCurrencyLabel(value);
            },
          },
        },
      },
    },
  };
}

function buildDemandChart(payload) {
  const series = trimSeriesToValueWindow(
    Array.isArray(payload?.series) ? payload.series : [],
    ['demandIndex', 'demandIndex12MonthAverage'],
  );
  const labels = series.map((item) => item.periodLabel);
  const monthlyDemand = series.map((item) => item.demandIndex);
  const trailingDemand = series.map((item) => item.demandIndex12MonthAverage);

  return {
    data: {
      labels,
      datasets: [
        {
          label: 'Monthly Demand Index',
          data: monthlyDemand,
          borderColor: '#7c3aed',
          backgroundColor: 'rgba(124, 58, 237, 0.10)',
          fill: true,
          borderWidth: 2.2,
          pointRadius: 0,
          pointHoverRadius: 3,
          pointBackgroundColor: '#7c3aed',
          pointBorderWidth: 0,
          spanGaps: true,
          tension: 0.24,
        },
        {
          label: 'Trailing 12m Avg',
          data: trailingDemand,
          borderColor: '#475569',
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1.8,
          borderDash: [8, 6],
          pointRadius: 0,
          pointHoverRadius: 3,
          pointBackgroundColor: '#475569',
          pointBorderWidth: 0,
          spanGaps: true,
          tension: 0.2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#64748b',
            boxWidth: 18,
            boxHeight: 2,
            usePointStyle: false,
          },
        },
        tooltip: {
          itemSort: sortTooltipItemsByVisualOrder,
          callbacks: {
            label(context) {
              const numeric = Number(context.parsed.y);
              return `${context.dataset.label}: ${Number.isFinite(numeric) ? numeric.toFixed(1) : '—'}`;
            },
            afterBody(tooltipItems) {
              const monthlyHeatScore = Number(tooltipItems?.find(
                (item) => item?.dataset?.label === 'Monthly Market Heat Index'
              )?.parsed?.y);
              const heatLabel = describeMarketHeatLevel(monthlyHeatScore);
              if (!heatLabel) return [];
              return [`Market Type: ${heatLabel}`];
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#64748b',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            color: 'rgba(148, 163, 184, 0.18)',
          },
          ticks: {
            color: '#64748b',
            callback(value) {
              return Number(value).toFixed(0);
            },
          },
        },
      },
    },
  };
}

function buildMarketHeatChart(payload) {
  const series = trimSeriesToValueWindow(
    Array.isArray(payload?.series) ? payload.series : [],
    ['marketHeatIndex', 'marketHeatIndex12MonthAverage'],
  );
  const labels = series.map((item) => item.periodLabel);
  const monthlyHeat = series.map((item) => item.marketHeatIndex);
  const trailingHeat = series.map((item) => item.marketHeatIndex12MonthAverage);

  return {
    data: {
      labels,
      datasets: [
        {
          label: 'Monthly Market Heat Index',
          data: monthlyHeat,
          borderColor: '#dc2626',
          backgroundColor: 'rgba(220, 38, 38, 0.10)',
          fill: true,
          borderWidth: 2.2,
          pointRadius: 0,
          pointHoverRadius: 3,
          pointBackgroundColor: '#dc2626',
          pointBorderWidth: 0,
          spanGaps: true,
          tension: 0.24,
        },
        {
          label: 'Trailing 12m Avg',
          data: trailingHeat,
          borderColor: '#7f1d1d',
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1.8,
          borderDash: [8, 6],
          pointRadius: 0,
          pointHoverRadius: 3,
          pointBackgroundColor: '#7f1d1d',
          pointBorderWidth: 0,
          spanGaps: true,
          tension: 0.2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#64748b',
            boxWidth: 18,
            boxHeight: 2,
            usePointStyle: false,
          },
        },
        tooltip: {
          itemSort: sortTooltipItemsByVisualOrder,
          callbacks: {
            label(context) {
              const numeric = Number(context.parsed.y);
              return `${context.dataset.label}: ${Number.isFinite(numeric) ? numeric.toFixed(1) : '—'}`;
            },
            afterBody(tooltipItems) {
              const monthlyMarketHeatItem = (tooltipItems || []).find(
                (item) => item?.datasetIndex === 0 || item?.dataset?.label === 'Monthly Market Heat Index'
              );
              const marketType = describeMarketHeatLevel(monthlyMarketHeatItem?.parsed?.y);
              if (!marketType) return [];
              return [`Market Type: ${marketType}`];
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#64748b',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            color: 'rgba(148, 163, 184, 0.18)',
          },
          ticks: {
            color: '#64748b',
            callback(value) {
              return Number(value).toFixed(0);
            },
          },
        },
      },
    },
  };
}

function buildPriceCutChart(payload) {
  const series = trimSeriesToValueWindow(
    Array.isArray(payload?.series) ? payload.series : [],
    ['priceCutShare', 'priceCutShare12MonthAverage'],
  );
  const labels = series.map((item) => item.periodLabel);
  const monthlyCuts = series.map((item) => item.priceCutShare);
  const trailingCuts = series.map((item) => item.priceCutShare12MonthAverage);

  return {
    data: {
      labels,
      datasets: [
        {
          label: 'Monthly Price Cut Share',
          data: monthlyCuts,
          borderColor: '#b45309',
          backgroundColor: 'rgba(180, 83, 9, 0.10)',
          fill: true,
          borderWidth: 2.2,
          pointRadius: 0,
          pointHoverRadius: 3,
          pointBackgroundColor: '#b45309',
          pointBorderWidth: 0,
          spanGaps: true,
          tension: 0.24,
        },
        {
          label: 'Trailing 12m Avg',
          data: trailingCuts,
          borderColor: '#78350f',
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1.8,
          borderDash: [8, 6],
          pointRadius: 0,
          pointHoverRadius: 3,
          pointBackgroundColor: '#78350f',
          pointBorderWidth: 0,
          spanGaps: true,
          tension: 0.2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#64748b',
            boxWidth: 18,
            boxHeight: 2,
            usePointStyle: false,
          },
        },
        tooltip: {
          itemSort: sortTooltipItemsByVisualOrder,
          callbacks: {
            label(context) {
              return `${context.dataset.label}: ${formatPercentShareLabel(context.parsed.y, 2)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#64748b',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            color: 'rgba(148, 163, 184, 0.18)',
          },
          ticks: {
            color: '#64748b',
            callback(value) {
              return formatPercentShareLabel(value, 0);
            },
          },
        },
      },
    },
  };
}

function ChartQuestionBlock({ question, children, href, label, compact = false }) {
  return (
    <div className="gross-yield-timeline__chart-copy">
      <p className="gross-yield-timeline__question">
        <span className="gross-yield-timeline__question-label">Question:</span>
        <span className="gross-yield-timeline__question-text">{question}</span>
        {compact ? (
          <a
            className="gross-yield-timeline__doc-link gross-yield-timeline__question-link"
            href={href}
            target="_blank"
            rel="noreferrer"
          >
            {label}
          </a>
        ) : null}
      </p>
      {!compact ? (
        <p className="gross-yield-timeline__chart-description">
          {children}{' '}
          <a
            className="gross-yield-timeline__doc-link"
            href={href}
            target="_blank"
            rel="noreferrer"
          >
            {label}
          </a>
        </p>
      ) : null}
    </div>
  );
}

export function ZillowTabbedTimelineDemo({
  loading,
  error,
  payload,
  variant = 'standard',
  headerControls = null,
  onRetry = null,
}) {
  const compact = variant === 'compact';
  const [activeTab, setActiveTab] = useState('prices');
  const series = Array.isArray(payload?.series) ? payload.series : [];
  const coverageItems = buildCoverageStatusItems(payload, series);
  const tabs = [
    {
      key: 'prices',
      label: 'Rent + Prices',
      title: 'Rent vs. Home Prices + Bottom-Tier Home Price',
      question: 'Are rents near 1% of home prices, and what is the lower-tier price point for this area?',
      compactQuestion: 'Are rents near 1% of prices? What is the lower-tier price?',
      description: 'This layers Zillow ZHVI, bottom-tier ZHVI, and ZORI by month. The rent axis is scaled so matching vertical height means monthly rent is exactly 1% of price.',
      sourceHref: ZILLOW_HOUSING_DATA_DOCS_URL,
      sourceLabel: 'ZHVI + ZORI docs',
      chart: series.filter((item) => (
        item?.homePrice != null || item?.bottomTierHomePrice != null || item?.rentPrice != null
      )).length >= 2 ? buildHomeRentChart(payload) : null,
      empty: 'Not enough Zillow price and rent history is loaded for this market yet.',
    },
    {
      key: 'rule',
      label: '1% Rule',
      title: 'Monthly 1% Rule',
      question: 'Does this market’s 1% rule trend fit our cash-flow goals?',
      compactQuestion: 'Does this market’s 1% rule trend fit our goals?',
      description: 'Monthly 1% rule here is Zillow rent divided by Zillow home price. It is the same rent-to-price relationship as gross yield, but not annualized.',
      sourceHref: ZILLOW_HOUSING_DATA_DOCS_URL,
      sourceLabel: 'Zillow source docs',
      chart: series.some((item) => item?.ruleRatio != null) ? buildRuleRatioChart(payload) : null,
      empty: 'Matching Zillow rent and home-price history is not loaded for this market yet.',
    },
    {
      key: 'demand',
      label: 'Renter Demand',
      title: 'Rental Demand Index — ZORDI',
      question: 'Are renters actively engaging with rentals in this market on Zillow?',
      compactQuestion: 'Is renter demand strong on Zillow?',
      description: 'ZORDI tracks Zillow rental-listing engagement as a proxy for renter demand. Higher values suggest stronger listing interest.',
      sourceHref: ZILLOW_HOUSING_DATA_DOCS_URL,
      sourceLabel: 'ZORDI docs',
      chart: series.filter((item) => item?.demandIndex != null).length >= 2
        ? buildDemandChart(payload)
        : null,
      empty: 'Zillow rental-demand history is not loaded for this market yet.',
    },
    {
      key: 'heat',
      label: 'Buy/Sell Market',
      title: 'For-Sale Market Heat Index',
      question: 'Does this region favor buyers or sellers?',
      compactQuestion: 'Does this market favor buyers or sellers?',
      description: 'Zillow’s index measures for-sale supply and demand: ≤27 strong buyer’s, 28–43 buyer’s, 44–54 neutral, 55–69 seller’s, and ≥70 strong seller’s market. See )',
      sourceHref: ZILLOW_MARKET_HEAT_METHODOLOGY_URL,
      sourceLabel: 'methodology',
      chart: series.filter((item) => item?.marketHeatIndex != null).length >= 2
        ? buildMarketHeatChart(payload)
        : null,
      empty: 'Zillow market-heat history is not loaded for this market yet.',
    },
    {
      key: 'cuts',
      label: 'Price Cuts',
      title: 'Share of Listings With a Price Cut',
      question: 'Are sellers having to cut prices more often here?',
      compactQuestion: 'Are sellers cutting prices more often?',
      description: "The number of unique properties with a list price at the end of the month that's less than the list price at the beginning of the month, divided by the number of unique properties with an active listing at some point during the month.",
      sourceHref: ZILLOW_HOUSING_DATA_DOCS_URL,
      sourceLabel: 'Zillow data docs',
      chart: series.filter((item) => item?.priceCutShare != null).length >= 2
        ? buildPriceCutChart(payload)
        : null,
      empty: 'Zillow price-cut history is not loaded for this market yet.',
    },
  ];
  const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.key === activeTab));
  const active = tabs[activeIndex];

  function selectRelativeTab(direction) {
    const nextIndex = (activeIndex + direction + tabs.length) % tabs.length;
    setActiveTab(tabs[nextIndex].key);
  }

  return (
    <article
      className={`${compact ? '' : 'research-detail-card '}zillow-tabbed-demo${compact ? ' zillow-tabbed-demo--compact' : ''}`}
      aria-labelledby="zillow-tabbed-demo-title"
    >
      <div className="zillow-tabbed-demo__header">
        <div>
          <span className="zillow-tabbed-demo__eyebrow">Layout demo</span>
          <h2 id="zillow-tabbed-demo-title">Zillow Market Trends</h2>
        </div>
        {headerControls}
        {!loading && !error ? (
          <div className="gross-yield-timeline__status">
            {coverageItems.map((item) => (
              <div key={item.label} className="gross-yield-timeline__status-pill">
                <span className="gross-yield-timeline__status-label">{item.label}</span>
                <span className="gross-yield-timeline__status-value">{item.value}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="zillow-tabbed-demo__tabs" role="tablist" aria-label="Zillow market trend charts">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            id={`zillow-demo-tab-${tab.key}`}
            type="button"
            role="tab"
            aria-selected={active.key === tab.key}
            aria-controls="zillow-demo-panel"
            tabIndex={active.key === tab.key ? 0 : -1}
            className={`zillow-tabbed-demo__tab${active.key === tab.key ? ' is-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                selectRelativeTab(1);
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                selectRelativeTab(-1);
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        id="zillow-demo-panel"
        className={`zillow-tabbed-demo__panel${loading ? ' is-loading' : ''}`}
        role="tabpanel"
        aria-labelledby={`zillow-demo-tab-${active.key}`}
      >
        {loading ? (
          compact ? (
            <div className="zillow-tabbed-demo__inline-loading" role="status" aria-live="polite">
              Loading Zillow history…
            </div>
          ) : <PageLoader compact label="Loading Zillow history" />
        ) : null}
        {!loading && error ? (
          <div className="research-detail-state research-detail-state--error">
            {error}
            {typeof onRetry === 'function' ? <button type="button" className="research-detail-state__retry" onClick={onRetry}>Try again</button> : null}
          </div>
        ) : null}
        {!loading && !error ? (
          <>
            <div className="zillow-tabbed-demo__chart-head">
              <div>
                {/* <h3>{active.title}</h3> */}
                <ChartQuestionBlock
                  question={compact ? active.compactQuestion || active.question : active.question}
                  href={active.sourceHref}
                  label={active.sourceLabel}
                  compact={compact}
                >
                  {active.description}
                </ChartQuestionBlock>
              </div>
            </div>
            {active.chart ? (
              <div className="zillow-tabbed-demo__chart">
                <Line
                  data={active.chart.data}
                  options={compact
                    ? buildCompactTimelineChartOptions(active.chart.options, {
                      tightenBottomSpacing: true,
                      xTickPadding: active.key === 'prices' ? 1 : undefined,
                      legendItemPadding: 10,
                    })
                    : active.chart.options}
                />
              </div>
            ) : (
              <div className="gross-yield-timeline__chart-empty">{active.empty}</div>
            )}
          </>
        ) : null}
      </div>
    </article>
  );
}

export default function GrossYieldTimelineSection({ loading, error, payload }) {
  const series = Array.isArray(payload?.series) ? payload.series : [];
  const ruleRatioPointCount = series.filter((item) => item?.ruleRatio != null).length;
  const coverageItems = buildCoverageStatusItems(payload, series);
  const homeRentPointCount = series.filter((item) => (
    item?.homePrice != null || item?.bottomTierHomePrice != null || item?.rentPrice != null
  )).length;
  const chart = ruleRatioPointCount >= 1
    ? buildRuleRatioChart(payload)
    : null;
  const homeRentChart = homeRentPointCount >= 2
    ? buildHomeRentChart(payload)
    : null;
  const demandPointCount = series.filter((item) => item?.demandIndex != null).length;
  const demandChart = demandPointCount >= 2
    ? buildDemandChart(payload)
    : null;
  const marketHeatPointCount = series.filter((item) => item?.marketHeatIndex != null).length;
  const marketHeatChart = marketHeatPointCount >= 2
    ? buildMarketHeatChart(payload)
    : null;
  const priceCutPointCount = series.filter((item) => item?.priceCutShare != null).length;
  const priceCutChart = priceCutPointCount >= 2
    ? buildPriceCutChart(payload)
    : null;
  const hasAnyChartData = (
    ruleRatioPointCount >= 1
    || homeRentPointCount >= 2
    || demandPointCount >= 2
    || marketHeatPointCount >= 2
    || priceCutPointCount >= 2
  );

  return (
    <article id="gross-yield-timeline-card" className="research-detail-card research-detail-card--gross-yield-timeline">
      <div className="gross-yield-timeline__header">
        <div>
          <h2 className="gross-yield-timeline__title">1% Rule Timeline</h2>
        </div>
        {!loading && !error ? (
          <div className="gross-yield-timeline__status">
            {coverageItems.map((item) => (
              <div key={item.label} className="gross-yield-timeline__status-pill">
                <span className="gross-yield-timeline__status-label">{item.label}</span>
                <span className="gross-yield-timeline__status-value">{item.value}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="research-detail-state">Loading 1% rule history…</div>
      ) : null}

      {!loading && error ? (
        <div className="research-detail-state research-detail-state--error">{error}</div>
      ) : null}

      {!loading && !error && !hasAnyChartData ? (
        <div className="gross-yield-timeline__empty">
          Zillow chart history is not available for this market yet.
        </div>
      ) : null}

      {!loading && !error && hasAnyChartData ? (
        <>
          <div className="gross-yield-timeline__chart-card">
            <div className="gross-yield-timeline__chart-head">
              <div>
                <h3>Monthly 1% Rule</h3>
                <ChartQuestionBlock
                  question="Does this market’s 1% rule trend fit our cash-flow goals?"
                  href={ZILLOW_HOUSING_DATA_DOCS_URL}
                  label="Zillow source docs"
                >
                  Monthly 1% rule here is Zillow rent divided by Zillow home price. It is the same rent-to-price relationship as gross yield, but not annualized.
                </ChartQuestionBlock>
              </div>
            </div>
            {chart ? (
              <>
                {ruleRatioPointCount === 1 ? (
                  <div className="gross-yield-timeline__chart-empty">
                    Only 1 Zillow rent-and-home month is currently loaded here, so this card can show the latest point but not a full trend line yet.
                  </div>
                ) : null}

                <div className="gross-yield-timeline__chart-wrap">
                  <Line data={chart.data} options={chart.options} />
                </div>
              </>
            ) : (
              <div className="gross-yield-timeline__chart-empty">
                Matching Zillow rent and home-price history is not loaded for this market yet, so the 1% rule timeline is unavailable.
              </div>
            )}
          </div>

          <div className="gross-yield-timeline__chart-card">
            <div className="gross-yield-timeline__chart-head">
              <div>
                <h3>Rent vs. Home Prices + Bottom-Tier Home Price</h3>
                <ChartQuestionBlock
                  question="Are rents near 1% of home prices, and what is the lower-tier price point for this area?"
                  href={ZILLOW_HOUSING_DATA_DOCS_URL}
                  label="ZHVI + ZORI docs"
                >
                  This layers Zillow ZHVI, bottom-tier ZHVI, and ZORI by month. The rent axis is scaled so matching vertical height means monthly rent is exactly 1% of price.
                </ChartQuestionBlock>
              </div>
            </div>
            {homeRentChart ? (
              <div className="gross-yield-timeline__chart-wrap">
                <Line data={homeRentChart.data} options={homeRentChart.options} />
              </div>
            ) : (
              <div className="gross-yield-timeline__chart-empty">
                Not enough monthly Zillow history is loaded yet to draw the home-price and rent chart.
              </div>
            )}
          </div>

          <div className="gross-yield-timeline__chart-card">
            <div className="gross-yield-timeline__chart-head">
              <div>
                <h3>Rental Demand Index — ZORDI</h3>
                <ChartQuestionBlock
                  question="Are renters actively engaging with rentals in this market on Zillow?"
                  href={ZILLOW_HOUSING_DATA_DOCS_URL}
                  label="ZORDI docs"
                >
                  ZORDI tracks Zillow rental-listing engagement as a proxy for renter demand. Higher values suggest stronger listing interest.
                </ChartQuestionBlock>
              </div>
            </div>
            {demandChart ? (
              <div className="gross-yield-timeline__chart-wrap">
                <Line data={demandChart.data} options={demandChart.options} />
              </div>
            ) : (
              <div className="gross-yield-timeline__chart-empty">
                Demand history is not loaded for this market yet.
              </div>
            )}
          </div>

          <div className="gross-yield-timeline__chart-card">
            <div className="gross-yield-timeline__chart-head">
              <div>
                <h3>For-Sale Market Heat Index</h3>
                <ChartQuestionBlock
                  question="How competitive is this market for acquisitions right now?"
                  href={ZILLOW_MARKET_HEAT_METHODOLOGY_URL}
                  label="methodology"
                >
                  Zillow’s Market Heat Index tracks for-sale supply and demand balance. A score of 70 or above to be a “strong sellers market.” A score from 55 to 69 to be a “sellers market.” A score from 44 to 55 to be a “neutral market.” A score from 28 to 44 to be a “buyers market.” A score of 27 or below to be a “strong buyers market.” Zillow blends listing engagement and listing-performance inputs. See
                </ChartQuestionBlock>
              </div>
            </div>
            {marketHeatChart ? (
              <div className="gross-yield-timeline__chart-wrap">
                <Line data={marketHeatChart.data} options={marketHeatChart.options} />
              </div>
            ) : (
              <div className="gross-yield-timeline__chart-empty">
                Market heat history is not loaded for this market yet.
              </div>
            )}
          </div>

          <div className="gross-yield-timeline__chart-card">
            <div className="gross-yield-timeline__chart-head">
              <div>
                <h3>Share of Listings With a Price Cut</h3>
                <ChartQuestionBlock
                  question="Are sellers having to cut prices more often here?"
                  href={ZILLOW_HOUSING_DATA_DOCS_URL}
                  label="Zillow data docs"
                >
                  The number of unique properties with a list price at the end of the month that's less than the list price at the beginning of the month, divided by the number of unique properties with an active listing at some point during the month.
                </ChartQuestionBlock>
              </div>
            </div>
            {priceCutChart ? (
              <div className="gross-yield-timeline__chart-wrap">
                <Line data={priceCutChart.data} options={priceCutChart.options} />
              </div>
            ) : (
              <div className="gross-yield-timeline__chart-empty">
                Price-cut history is not loaded for this market yet.
              </div>
            )}
          </div>
        </>
      ) : null}
    </article>
  );
}
